import React from 'react';
import axios from 'axios';

import { ConfirmModal } from '~/components/ConfirmModal';
import Chip from '~/components/core/Atomic/Chip/Chip';
import Heading from '~/components/core/TextComponents/Heading';
import ThreeDotsMenu from '~/components/core/ThreeDotsMenu';
import type { Profile } from '~/components/SystemConfiguration/Communications/PhoneConfigurationTab/phoneProfilesApi';
import {
  deletePhoneProfile,
  promotePhoneProfileToDefault,
} from '~/components/SystemConfiguration/Communications/PhoneConfigurationTab/phoneProfilesApi';
import { reportAxiosError } from '~/Utils';

import DeleteProfileInUseModal from './DeleteProfileInUseModal';
import RenameProfileDialog from './RenameProfileDialog';
import SetDefaultProfileConfirmModal from './SetDefaultProfileConfirmModal';

interface PhoneProfileDetailHeaderProps {
  profile: Profile;
  organizationId: number;
  onMutated: () => Promise<unknown>;
  onDeleted: () => void;
  reloadOperationalDetails: () => Promise<unknown>;
}

const PhoneProfileDetailHeader: React.FC<PhoneProfileDetailHeaderProps> = ({
  profile,
  organizationId,
  onMutated,
  onDeleted,
  reloadOperationalDetails,
}) => {
  const [isRenameOpen, setIsRenameOpen] = React.useState(false);
  const [isSetDefaultOpen, setIsSetDefaultOpen] = React.useState(false);
  const [isSetDefaultSubmitting, setIsSetDefaultSubmitting] = React.useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = React.useState(false);
  const [isDeleteSubmitting, setIsDeleteSubmitting] = React.useState(false);
  const [deleteInUseState, setDeleteInUseState] = React.useState<{ isOpen: boolean; assignedUserCount: number }>({
    isOpen: false,
    assignedUserCount: 0,
  });

  const handleSetDefault = async () => {
    setIsSetDefaultSubmitting(true);
    try {
      await promotePhoneProfileToDefault(organizationId, profile.id);
      await onMutated();
      await reloadOperationalDetails();
      setIsSetDefaultOpen(false);
    } catch (error) {
      reportAxiosError(error);
    } finally {
      setIsSetDefaultSubmitting(false);
    }
  };

  const handleDeleteConfirmed = async () => {
    setIsDeleteSubmitting(true);
    try {
      await deletePhoneProfile(organizationId, profile.id);
      // Refresh the profile list BEFORE navigating back so the list view doesn't render
      // a stale row for the just-deleted profile.
      await onMutated();
      setIsDeleteConfirmOpen(false);
      onDeleted();
    } catch (error) {
      setIsDeleteConfirmOpen(false);
      const reasons: unknown = axios.isAxiosError(error)
        ? (error.response?.data as { reasons?: unknown } | undefined)?.reasons
        : undefined;
      if (Array.isArray(reasons) && reasons.length > 0) {
        setDeleteInUseState({ isOpen: true, assignedUserCount: reasons.length });
        return;
      }
      reportAxiosError(error);
    } finally {
      setIsDeleteSubmitting(false);
    }
  };

  const menuOptions = [
    {
      key: 'rename',
      node: 'Rename',
      onClick: () => setIsRenameOpen(true),
    },
    {
      key: 'set-default',
      node: 'Set as default',
      disabled: profile.is_default,
      onClick: () => setIsSetDefaultOpen(true),
    },
    {
      key: 'delete',
      node: 'Delete',
      disabled: profile.is_default,
      onClick: () => setIsDeleteConfirmOpen(true),
    },
  ];

  return (
    <div className="flex items-center justify-between pb-20">
      <div className="flex items-center gap-12">
        <Heading variant={Heading.TYPES.H2}>{profile.name}</Heading>
        {profile.is_default ? <Chip size="small" label="Default" /> : null}
      </div>
      <ThreeDotsMenu options={menuOptions} />
      <RenameProfileDialog
        isOpen={isRenameOpen}
        organizationId={organizationId}
        profileId={profile.id}
        currentName={profile.name}
        onClose={() => setIsRenameOpen(false)}
        onRenamed={onMutated}
      />
      <SetDefaultProfileConfirmModal
        isOpen={isSetDefaultOpen}
        isSubmitting={isSetDefaultSubmitting}
        profileName={profile.name}
        onClose={() => setIsSetDefaultOpen(false)}
        onConfirm={handleSetDefault}
      />
      <ConfirmModal
        isOpen={isDeleteConfirmOpen}
        isSubmitting={isDeleteSubmitting}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onPrimaryBtnClick={handleDeleteConfirmed}
        title="Delete phone profile"
        contentText={`Are you sure you want to delete "${profile.name}"? This cannot be undone.`}
        primaryButtonName="Delete"
        secondaryButtonName="Cancel"
      />
      <DeleteProfileInUseModal
        isOpen={deleteInUseState.isOpen}
        assignedUserCount={deleteInUseState.assignedUserCount}
        onClose={() => setDeleteInUseState({ isOpen: false, assignedUserCount: 0 })}
      />
    </div>
  );
};

export default PhoneProfileDetailHeader;
